<template>

    <nav class="flex flex-1 flex-col">
        <ul
            role="list"
            :class="getNavigationListClass()"
        >
            <li v-for="section in navigationSections" :key="section.key">
                <div
                    v-if="section.label && (!collapsed || fixedRail)"
                    :class="getSectionLabelClass()"
                    :aria-hidden="fixedRail && collapsed ? 'true' : undefined"
                >
                    {{ section.label }}
                </div>
                <ul
                    role="list"
                    class="space-y-1"
                    :class="getSectionItemsClass(section)"
                >
                    <li v-for="item in section.items" :key="item.name">
                        <RouterLink
                            :to="item.href"
                            :class="getNavItemClass(item)"
                            :aria-label="item.name"
                            :title="collapsed ? item.name : undefined"
                        >
                            <span
                                v-if="fixedRail && item.current"
                                :class="getNavActiveSelectorClass()"
                                aria-hidden="true"
                            ></span>
                            <component :is="item.icon" :class="getNavIconClass(item)" aria-hidden="true" />
                            <span
                                v-if="fixedRail || !collapsed"
                                :class="getNavLabelClass()"
                                :aria-hidden="fixedRail && collapsed ? 'true' : undefined"
                            >
                                {{ item.name }}
                            </span>
                            <span
                                v-if="!collapsed && getNavBadge(item)"
                                :class="getNavBadgeClass()"
                                :aria-hidden="fixedRail && collapsed ? 'true' : undefined"
                            >
                                {{ getNavBadge(item) }}
                            </span>
                            <span
                                v-if="collapsed && getNavBadge(item)"
                                class="absolute right-1.5 top-1.5 z-10 size-2 rounded-full bg-amber-400 ring-2 ring-indigo-950"
                            >
                                <span class="sr-only">{{ getNavBadge(item) }}</span>
                            </span>
                        </RouterLink>
                    </li>
                </ul>
            </li>

            <!--
            <li class="-mx-2">
                <RouterLink
                    :to="{ name: 'configuration' }"
                    :class="[route.name === 'configuration' ? 'bg-indigo-700 text-white' : 'text-indigo-200 hover:bg-indigo-700 hover:text-white', 'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold']"
                >
                    <Cog6ToothIcon
                        :class="[route.name === 'configuration' ? 'text-white' : 'text-indigo-200 group-hover:text-white', 'size-6 shrink-0']"
                        aria-hidden="true"
                    />
                    Settings
                </RouterLink>
            </li>
            -->

        </ul>
    </nav>

</template>

<script setup>

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
    Cog6ToothIcon,
    CalendarDaysIcon,
    ClipboardDocumentListIcon,
    HomeIcon,
    InboxStackIcon,
    LinkIcon,
    UserGroupIcon,
    BuildingStorefrontIcon,
    LanguageIcon,
    PaperClipIcon,
    PhotoIcon,
    PercentBadgeIcon,
    EnvelopeIcon,
    RectangleGroupIcon,
    ChartBarIcon,
    DocumentCurrencyDollarIcon,
    PrinterIcon,
    ScaleIcon,
    SwatchIcon, PaintBrushIcon, WrenchIcon, CurrencyDollarIcon, DocumentTextIcon, ShoppingBagIcon, TagIcon, TruckIcon,
    PaperAirplaneIcon, RectangleStackIcon, FolderIcon, ShoppingCartIcon, TableCellsIcon, CubeIcon, CalculatorIcon,
    LockClosedIcon, MapPinIcon, DocumentIcon, Square3Stack3DIcon, ViewColumnsIcon, Bars3Icon,
    ChatBubbleLeftRightIcon,
    CpuChipIcon,
    PhoneIcon,
    SparklesIcon,
} from '@heroicons/vue/24/outline'
import { activeTenantModules, rememberTenantActiveModules, rememberTenantOfferSetup, tenantHasModule } from '@/composables/useTenantModules.js'

const route     = useRoute()
const router    = useRouter()
const { t } = useI18n()

const props = defineProps({
    collapsed: {
        type: Boolean,
        default: false,
    },
    fixedRail: {
        type: Boolean,
        default: false,
    },
})
const collapsed = computed(() => props.collapsed)
const fixedRail = computed(() => props.fixedRail)

const { result } = useQuery(gql`
    query {
        currentTenant {
            id
            type
            active_modules
            offer_setup_required
            problem_discovery_enabled
            internal_chat_enabled
            apps_enabled
        }
    }
`)

watch([() => route.params.tenantId, result], ([tenantId, value]) => {
    if (!value?.currentTenant) {
        return
    }

    rememberTenantActiveModules(tenantId, activeTenantModules(value.currentTenant))
    rememberTenantOfferSetup(tenantId, value.currentTenant.offer_setup_required)
}, { immediate: true })

const tenantType = computed(() => result.value?.currentTenant?.type)
const currentTenantId = computed(() => result.value?.currentTenant?.id)
const isTamepulseTenant = computed(() => tenantType.value === 'tamepulse')
const hasTenantModule = (moduleKey) => tenantHasModule(result.value?.currentTenant, moduleKey)
const hasTamepulseModule = (moduleKey) => isTamepulseTenant.value && hasTenantModule(moduleKey)
const hasApprovalsModule = computed(() => hasTamepulseModule('approvals'))
const isProblemDiscoveryEnabled = () => !!result.value?.currentTenant?.problem_discovery_enabled
const pendingApprovalCount = ref(0)
const approvalChannelName = ref(null)

const { result: pendingApprovalsResult } = useQuery(gql`
    query ($first: Int!, $page: Int, $status: String) {
        tamepulseApprovalRequestPaginated(perPage: $first, page: $page, status: $status) {
            paginatorInfo {
                total
            }
        }
    }
`, {
    first: 1,
    page: 1,
    status: 'pending',
}, {
    enabled: hasApprovalsModule,
    fetchPolicy: 'cache-and-network',
})

const setPendingApprovalCount = (value) => {
    pendingApprovalCount.value = Math.max(0, Number(value ?? 0))
}

watch(pendingApprovalsResult, (value) => {
    setPendingApprovalCount(value?.tamepulseApprovalRequestPaginated?.paginatorInfo?.total)
}, { immediate: true })

const subscribeToApprovalBadge = () => {
    leaveApprovalBadgeChannel()

    if (!window.Echo || !hasApprovalsModule.value || !currentTenantId.value) {
        return
    }

    const channelName = `tamepulse.approvals.${currentTenantId.value}`
    approvalChannelName.value = channelName

    window.Echo.channel(channelName)
        .listen('.TamepulseApprovalRequestsUpdated', (event) => {
            setPendingApprovalCount(event.pending_count)
        })
}

const leaveApprovalBadgeChannel = () => {
    if (window.Echo && approvalChannelName.value) {
        window.Echo.leave(approvalChannelName.value)
    }

    approvalChannelName.value = null
}

watch([hasApprovalsModule, currentTenantId], subscribeToApprovalBadge, { immediate: true })

onBeforeUnmount(leaveApprovalBadgeChannel)

const isNicheraTenant = () => result.value?.currentTenant?.type === 'nichera'
const hasSalesCrmModules = () => tenantHasModule(result.value?.currentTenant, 'quote')
const hasTicketsModule = () => hasTenantModule('tickets')
const hasInvoicesModule = () => hasTenantModule('invoices')
const isReceptionTenant = () => result.value?.currentTenant?.type === 'reception'
const isExpenseManagerTenant = () => result.value?.currentTenant?.type === 'expense_manager'

const getNavBadge = (item) => {
    const badge = Number(item.badge ?? 0)

    if (badge <= 0) {
        return null
    }

    return badge > 99 ? '99+' : String(badge)
}

const getNavItemClass = (item) => {
    if (fixedRail.value) {
        return [
            item.current
                ? 'text-white'
                : 'text-indigo-100/85 hover:bg-white/10 hover:text-white',
            collapsed.value
                ? 'mx-1 w-11 grid-cols-[2.75rem_minmax(0,0fr)_auto]'
                : 'w-full grid-cols-[3.25rem_minmax(0,1fr)_auto]',
            'group relative grid h-11 items-center rounded-xl text-sm font-semibold transition-colors duration-150',
        ]
    }

    return [
        item.current
            ? 'bg-white/16 text-white shadow-sm ring-1 ring-white/10'
            : 'text-indigo-100/85 hover:bg-white/10 hover:text-white',
        collapsed.value
            ? 'h-11 justify-center px-0'
            : 'h-11 gap-x-3 px-3',
        'group relative flex items-center rounded-xl text-sm font-semibold transition duration-150',
    ]
}

const getNavActiveSelectorClass = () => {
    return [
        'pointer-events-none absolute inset-y-0 rounded-xl bg-white/16 shadow-sm ring-1 ring-inset ring-white/15',
        collapsed.value ? 'inset-x-0' : 'left-1 right-0',
    ]
}

const getNavigationListClass = () => {
    if (fixedRail.value) {
        return [
            'flex flex-1 flex-col',
            'gap-y-5',
        ]
    }

    return [
        'flex flex-1 flex-col transition-[gap] duration-200',
        collapsed.value ? 'gap-y-2' : 'gap-y-5',
    ]
}

const getSectionLabelClass = () => {
    return [
        'px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-200/70',
        fixedRail.value
            ? [
                'h-4 overflow-hidden transition-opacity duration-150 ease-out',
                collapsed.value ? 'opacity-0' : 'opacity-100',
            ]
            : '',
    ]
}

const getSectionItemsClass = (section) => {
    if (fixedRail.value) {
        return section.label ? 'mt-2' : 'mt-0'
    }

    return [
        collapsed.value ? 'mx-0' : '-mx-2',
        !collapsed.value && section.label ? 'mt-2' : 'mt-0',
    ]
}

const getNavIconClass = (item) => {
    return [
        item.current
            ? 'text-white'
            : 'text-indigo-200/80 group-hover:text-white',
        fixedRail.value
            ? 'relative z-10 col-start-1 size-5 shrink-0 justify-self-center transition-colors'
            : 'size-5 shrink-0 transition',
    ]
}

const getNavLabelClass = () => {
    return [
        'min-w-0 truncate',
        fixedRail.value
            ? [
                'relative z-10 transition-[opacity,transform] duration-150 ease-out',
                collapsed.value ? 'pointer-events-none translate-x-1 opacity-0' : 'translate-x-0 opacity-100',
            ]
            : 'flex-1',
    ]
}

const getNavBadgeClass = () => {
    return [
        'inline-flex min-w-5 items-center justify-center rounded-full bg-amber-400 px-1.5 py-0.5 text-[11px] font-bold leading-none text-slate-950',
        fixedRail.value
            ? [
                'relative z-10 col-start-3 mr-2 transition-[opacity,transform] duration-150 ease-out',
                collapsed.value ? 'pointer-events-none translate-x-1 opacity-0' : 'translate-x-0 opacity-100',
            ]
            : 'ml-auto',
    ]
}

const visibleNavigationItems = (items) => items.filter((item) => item.visible !== false)

const navigationSections = computed(() => [
    {
        key: 'main',
        label: null,
        items: visibleNavigationItems(getNavigation()),
    },
    ...getQuoteNavigationSections(),
    {
        key: 'localize',
        label: 'Localize',
        items: hasLocalizeService() ? visibleNavigationItems(getLocalizeNavigation()) : [],
    },
    {
        key: 'builder',
        label: 'Builder',
        items: visibleNavigationItems(getBuilderNavigation()),
    },
    {
        key: 'slotflow',
        label: 'Slotflow',
        items: visibleNavigationItems(getSlotflowNavigation()),
    },
    {
        key: 'lead-capture',
        label: 'Lead Capture',
        items: visibleNavigationItems(getLeadCaptureNavigation()),
    },
    {
        key: 'review-widget',
        label: 'Review Widget',
        items: visibleNavigationItems(getReviewWidgetNavigation()),
    },
    {
        key: 'tickets',
        label: 'Tickets',
        items: visibleNavigationItems(getTicketsNavigation()),
    },
    {
        key: 'reception',
        label: t('reception.navigation.section'),
        items: visibleNavigationItems(getReceptionNavigation()),
    },
    {
        key: 'expense-manager',
        label: t('expenseManager.navigation.section'),
        items: visibleNavigationItems(getExpenseManagerNavigation()),
    },
    {
        key: 'tools',
        label: 'Tools',
        items: visibleNavigationItems(getToolNavigation()),
    },
    {
        key: 'modules',
        label: null,
        items: visibleNavigationItems(getModuleNavigation()),
    },
].filter((section) => section.items.length > 0))

const getNavigation     = () => {

    let navigation      = [
        {
            name        : t('tamepulse.navigation.dashboard'),
            href        : router.resolve({ name: 'dashboard.index' }).href,
            icon        : HomeIcon,
            current     : route.name === 'dashboard.index',
            visible     : true
        },
        {
            name        : t('tamepulse.navigation.workspaces'),
            href        : route.params.tenantId
                ? router.resolve({ name: 'workspaces.index', params: { tenantId: route.params.tenantId } }).href
                : router.resolve({ name: 'workspaces.global' }).href,
            icon        : Square3Stack3DIcon,
            current     : ['workspaces.global', 'workspaces.index'].includes(route.name),
            visible     : true
        }
    ]

    navigation.push({
        name        : t('appCatalog.title'),
        href        : router.resolve({ name: 'apps.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : CubeIcon,
        current     : route.name?.startsWith('apps.') || route.name?.startsWith('app.entity.'),
        visible     : result.value?.currentTenant?.apps_enabled === true,
    })

    navigation.push({
        name        : 'Calcura',
        href        : router.resolve({ name: 'calcura.calculator.index' }).href,
        icon        : CalculatorIcon,
        current     : route.name === 'calcura.calculator.index',
        visible     : result.value?.currentTenant?.type === 'calcura'
    })

    navigation.push({
        name        : 'Stores',
        href        : router.resolve({ name: 'store.index' }).href,
        icon        : MapPinIcon,
        current     : route.name?.startsWith('store.'),
        visible     : result.value?.currentTenant?.type === 'store_locator'
    })

    if (result.value?.currentTenant?.type === 'tamepulse') {
        navigation.push(...getTamepulseNavigation())
    }

    navigation.push({
        name        : 'Customers',
        href        : router.resolve({ name: 'customer.index' }).href,
        icon        : UserGroupIcon,
        current     : route.name === 'customer.index',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Product categories',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'category' } }).href,
        icon        : FolderIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'category',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Products',
        href        : router.resolve({ name: 'product.index' }).href,
        icon        : ShoppingBagIcon,
        current     : route.name === 'product.index',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Orders',
        href        : router.resolve({ name: 'order.index' }).href,
        icon        : ShoppingCartIcon,
        current     : route.name === 'order.index',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Media Library',
        href        : router.resolve({ name: 'media-library.index' }).href,
        icon        : PhotoIcon,
        current     : route.name === 'media-library.index',
        visible     : ['nichera', 'website'].includes(result.value?.currentTenant?.type)
    })

    navigation.push({
        name        : 'Discount groups',
        href        : router.resolve({ name: 'discount-group.index' }).href,
        icon        : RectangleGroupIcon,
        current     : route.name === 'discount-group.index',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Discounts',
        href        : router.resolve({ name: 'discount.index' }).href,
        icon        : PercentBadgeIcon,
        current     : route.name === 'discount.index',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Tags',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'tag' } }).href,
        icon        : TagIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'tag',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Courier',
        href        : router.resolve({ name: 'courier.index' }).href,
        icon        : TruckIcon,
        current     : route.name === 'courier.index',
        visible     : isNicheraTenant()
    })

    navigation.push({
        name        : 'Shipping taxes',
        href        : router.resolve({ name: 'shipping.index' }).href,
        icon        : PaperAirplaneIcon,
        current     : route.name === 'shipping.taxes',
        visible     : isNicheraTenant()
    })

    return navigation
}

const getBuilderNavigation = () => {

    const isBuilder = ['nichera', 'website'].includes(result.value?.currentTenant?.type)

    return [
        {
            name    : 'Pagine',
            href    : router.resolve({ name: 'entity.index', params: { entityType: 'page' } }).href,
            icon    : DocumentIcon,
            current : route.name?.startsWith('entity.') && route.params?.entityType === 'page',
            visible : isBuilder
        },
        {
            name    : 'Articoli',
            href    : router.resolve({ name: 'entity.index', params: { entityType: 'post' } }).href,
            icon    : ClipboardDocumentListIcon,
            current : route.name?.startsWith('entity.') && route.params?.entityType === 'post',
            visible : isBuilder
        },
        {
            name    : 'Categorie articoli',
            href    : router.resolve({ name: 'entity.index', params: { entityType: 'post_category' } }).href,
            icon    : FolderIcon,
            current : route.name?.startsWith('entity.') && route.params?.entityType === 'post_category',
            visible : isBuilder
        },
        {
            name    : 'Template',
            href    : router.resolve({ name: 'entity.index', params: { entityType: 'page_template' } }).href,
            icon    : RectangleStackIcon,
            current : route.name?.startsWith('entity.') && route.params?.entityType === 'page_template',
            visible : isBuilder
        },
        {
            name    : 'Global blocks',
            href    : router.resolve({ name: 'entity.index', params: { entityType: 'global_block' } }).href,
            icon    : ViewColumnsIcon,
            current : route.name?.startsWith('entity.') && route.params?.entityType === 'global_block',
            visible : isBuilder
        },
        {
            name    : 'Menu',
            href    : router.resolve({ name: 'menu.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : Bars3Icon,
            current : route.name?.startsWith('menu.'),
            visible : isBuilder
        },
        {
            name    : 'Categorie pagine',
            href    : router.resolve({ name: 'entity.index', params: { entityType: 'page_category' } }).href,
            icon    : FolderIcon,
            current : route.name?.startsWith('entity.') && route.params?.entityType === 'page_category',
            visible : isBuilder
        },
        {
            name    : 'Global settings',
            href    : router.resolve({ name: 'builder.global-settings', params: { tenantId: route.params.tenantId } }).href,
            icon    : PaintBrushIcon,
            current : route.name === 'builder.global-settings',
            visible : isBuilder
        },
        {
            name    : 'Themes',
            href    : router.resolve({ name: 'builder.themes', params: { tenantId: route.params.tenantId } }).href,
            icon    : SwatchIcon,
            current : route.name === 'builder.themes',
            visible : isBuilder
        },
        {
            name    : 'Site languages',
            href    : router.resolve({ name: 'locale-configuration', params: { tenantId: route.params.tenantId } }).href,
            icon    : LanguageIcon,
            current : route.name === 'locale-configuration',
            visible : isBuilder
        },
    ]
}

const getSlotflowNavigation = () => {
    const isSlotflow = result.value?.currentTenant?.type === 'slotflow'

    return [
        {
            name    : 'Forms',
            href    : router.resolve({ name: 'slotflow.form.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : CalendarDaysIcon,
            current : route.name?.startsWith('slotflow.form.'),
            visible : isSlotflow
        },
        {
            name    : 'Calendar connections',
            href    : router.resolve({ name: 'slotflow.calendar-connection.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : LinkIcon,
            current : route.name?.startsWith('slotflow.calendar-connection.'),
            visible : isSlotflow
        },
        {
            name    : 'Bookings',
            href    : router.resolve({ name: 'slotflow.booking.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : ClipboardDocumentListIcon,
            current : route.name?.startsWith('slotflow.booking.'),
            visible : isSlotflow
        },
        {
            name    : 'Submissions',
            href    : router.resolve({ name: 'slotflow.submission.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('slotflow.submission.'),
            visible : false
        },
    ]
}

const getLeadCaptureNavigation = () => {
    const isLeadCapture = result.value?.currentTenant?.type === 'lead_capture'

    return [
        {
            name    : 'Forms',
            href    : router.resolve({ name: 'lead-capture.form.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : DocumentTextIcon,
            current : route.name?.startsWith('lead-capture.form.'),
            visible : isLeadCapture
        },
        {
            name    : 'Submissions',
            href    : router.resolve({ name: 'lead-capture.submission.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('lead-capture.submission.'),
            visible : isLeadCapture
        },
    ]
}

const getReviewWidgetNavigation = () => {
    const isReviewWidget = result.value?.currentTenant?.type === 'review_widget'

    return [
        {
            name    : 'Forms',
            href    : router.resolve({ name: 'review-widget.form.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : DocumentTextIcon,
            current : route.name?.startsWith('review-widget.form.'),
            visible : isReviewWidget
        },
        {
            name    : 'Reviews',
            href    : router.resolve({ name: 'review-widget.review.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('review-widget.review.'),
            visible : isReviewWidget
        },
    ]
}

const getTicketsNavigation = () => {

    const hasTickets = hasTicketsModule()

    return [
        {
            name    : 'Tickets',
            href    : router.resolve({ name: 'ticket.index' }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('ticket.'),
            visible : hasTickets
        },
        {
            name    : 'Contracts',
            href    : router.resolve({ name: 'contract.index' }).href,
            icon    : DocumentTextIcon,
            current : route.name?.startsWith('contract.'),
            visible : hasTickets
        },
        {
            name    : 'Customers',
            href    : router.resolve({ name: 'customer.index' }).href,
            icon    : UserGroupIcon,
            current : route.name?.startsWith('customer.'),
            visible : hasTickets
        },
    ]
}

const getQuoteNavigation = () => {
    const isQuote = hasSalesCrmModules()

    return [
        {
            name    : t('quote.navigation.requests'),
            href    : router.resolve({ name: 'quote.sales-request.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('quote.sales-request.'),
            visible : isQuote,
            section : 'intake',
        },
        {
            name    : t('quote.navigation.quotes'),
            href    : router.resolve({ name: 'quote.quote.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : DocumentCurrencyDollarIcon,
            current : route.name?.startsWith('quote.quote.'),
            visible : isQuote,
            section : 'sales',
        },
        {
            name    : t('quote.navigation.invoices'),
            href    : router.resolve({ name: 'tamepulse.invoice.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : DocumentCurrencyDollarIcon,
            current : route.name?.startsWith('tamepulse.invoice.'),
            visible : isTamepulseTenant.value && hasInvoicesModule(),
            section : 'sales',
        },
        {
            name    : t('quote.navigation.accounts'),
            href    : router.resolve({ name: 'quote.account.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : BuildingStorefrontIcon,
            current : route.name?.startsWith('quote.account.'),
            visible : isQuote,
            section : 'contacts',
        },
        {
            name    : t('quote.navigation.people'),
            href    : router.resolve({ name: 'quote.person.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : UserGroupIcon,
            current : route.name?.startsWith('quote.person.'),
            visible : isQuote,
            section : 'contacts',
        },
        {
            name    : t('quote.navigation.followUps'),
            href    : router.resolve({ name: 'quote.follow-up.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : CalendarDaysIcon,
            current : route.name?.startsWith('quote.follow-up.'),
            visible : isQuote,
            section : 'activities',
        },
    ]
}

const getQuoteNavigationSections = () => {
    const navigation = getQuoteNavigation()

    return [
        {
            key: 'quote-intake',
            label: t('quote.navigation.sections.intake'),
            items: visibleNavigationItems(navigation.filter((item) => item.section === 'intake')),
        },
        {
            key: 'quote-sales',
            label: t('quote.navigation.sections.sales'),
            items: visibleNavigationItems(navigation.filter((item) => item.section === 'sales')),
        },
        {
            key: 'quote-contacts',
            label: t('quote.navigation.sections.contacts'),
            items: visibleNavigationItems(navigation.filter((item) => item.section === 'contacts')),
        },
        {
            key: 'quote-activities',
            label: t('quote.navigation.sections.activities'),
            items: visibleNavigationItems(navigation.filter((item) => item.section === 'activities')),
        },
    ]
}

const getReceptionNavigation = () => {
    const isReception = isReceptionTenant()

    return [
        {
            name    : t('reception.navigation.dashboard'),
            href    : router.resolve({ name: 'reception.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : ChartBarIcon,
            current : route.name === 'reception.index',
            visible : isReception
        },
        {
            name    : t('reception.navigation.calls'),
            href    : router.resolve({ name: 'reception.call.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : PhoneIcon,
            current : route.name?.startsWith('reception.call.'),
            visible : isReception
        },
    ]
}

const getExpenseManagerNavigation = () => {
    const isExpenseManager = isExpenseManagerTenant()

    return [
        {
            name    : t('expenseManager.navigation.accounts'),
            href    : router.resolve({ name: 'expense-manager.account.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : CurrencyDollarIcon,
            current : route.name?.startsWith('expense-manager.account.'),
            visible : isExpenseManager
        },
        {
            name    : t('expenseManager.navigation.transactions'),
            href    : router.resolve({ name: 'expense-manager.transaction.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : DocumentCurrencyDollarIcon,
            current : route.name?.startsWith('expense-manager.transaction.'),
            visible : isExpenseManager
        },
        {
            name    : t('expenseManager.navigation.runway'),
            href    : router.resolve({ name: 'expense-manager.runway.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : ChartBarIcon,
            current : route.name?.startsWith('expense-manager.runway.'),
            visible : isExpenseManager
        },
        {
            name    : t('expenseManager.navigation.forecastFlows'),
            href    : router.resolve({ name: 'expense-manager.forecast-flow.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : CalendarDaysIcon,
            current : route.name?.startsWith('expense-manager.forecast-flow.') || route.name?.startsWith('expense-manager.forecast-occurrence.'),
            visible : isExpenseManager
        },
        {
            name    : t('expenseManager.navigation.categories'),
            href    : router.resolve({ name: 'expense-manager.category.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : TagIcon,
            current : route.name?.startsWith('expense-manager.category.'),
            visible : isExpenseManager
        },
        {
            name    : t('expenseManager.navigation.rules'),
            href    : router.resolve({ name: 'expense-manager.category-rule.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : WrenchIcon,
            current : route.name?.startsWith('expense-manager.category-rule.'),
            visible : isExpenseManager
        },
        {
            name    : t('expenseManager.navigation.import'),
            href    : router.resolve({ name: 'expense-manager.import', params: { tenantId: route.params.tenantId } }).href,
            icon    : TableCellsIcon,
            current : route.name === 'expense-manager.import',
            visible : isExpenseManager
        },
    ]
}

const getTamepulseNavigation  = () => {

    return [
        {
            name: t('tamepulse.evaluations.title'),
            href: router.resolve({ name: 'tamepulse.evaluation.index' }).href,
            icon: SparklesIcon,
            current: route.name?.startsWith('tamepulse.evaluation.'),
            visible: hasTamepulseModule('chat')
        },
        {
            name    : t('tamepulse.navigation.connections'),
            href    : router.resolve({ name: 'tamepulse.connection' }).href,
            icon    : Cog6ToothIcon,
            current : route.name?.startsWith('tamepulse.connection'),
            visible : hasTamepulseModule('connections')
        },
        {
            name    : t('tamepulse.navigation.solutions'),
            href    : router.resolve({ name: 'tamepulse.solution.index' }).href,
            icon    : SparklesIcon,
            current : route.name?.startsWith('tamepulse.solution.'),
            visible : hasTamepulseModule('chat')
        },
        {
            name    : t('tamepulse.navigation.procedures'),
            href    : router.resolve({ name: 'tamepulse.procedure.index' }).href,
            icon    : WrenchIcon,
            current : route.name?.startsWith('tamepulse.procedure.'),
            visible : hasTamepulseModule('chat')
        },
        {
            name    : t('tamepulse.navigation.chat'),
            href    : router.resolve({ name: 'tamepulse.chat.index' }).href,
            icon    : ChatBubbleLeftRightIcon,
            current : route.name?.startsWith('tamepulse.chat.'),
            visible : result.value?.currentTenant?.internal_chat_enabled === true && hasTamepulseModule('chat')
        },
        {
            name    : t('tamepulse.navigation.conversations'),
            href    : router.resolve({ name: 'tamepulse.inbox.index' }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('tamepulse.inbox.'),
            visible : hasTamepulseModule('chat')
        },
        {
            name    : t('tamepulse.navigation.knowledgeBase'),
            href    : router.resolve({ name: 'tamepulse.knowledgeBase.index' }).href,
            icon    : FolderIcon,
            current : route.name?.startsWith('tamepulse.knowledgeBase.'),
            visible : hasTamepulseModule('knowledge_base')
        },
        {
            name    : t('tamepulse.navigation.mail'),
            href    : router.resolve({ name: 'tamepulse.mail.index' }).href,
            icon    : EnvelopeIcon,
            current : route.name?.startsWith('tamepulse.mail.'),
            visible : hasTamepulseModule('mail')
        },
        {
            name    : t('tamepulse.navigation.playbooks'),
            href    : router.resolve({ name: 'tamepulse.playbook.index' }).href,
            icon    : RectangleStackIcon,
            current : route.name?.startsWith('tamepulse.playbook.'),
            visible : hasTamepulseModule('playbooks')
        },
        {
            name    : t('tamepulse.navigation.templates'),
            href    : router.resolve({ name: 'tamepulse.template.index' }).href,
            icon    : Square3Stack3DIcon,
            current : route.name?.startsWith('tamepulse.template.'),
            visible : hasTamepulseModule('templates')
        },
        {
            name    : t('tamepulse.navigation.approvals'),
            href    : router.resolve({ name: 'tamepulse.approval.index' }).href,
            icon    : InboxStackIcon,
            current : route.name?.startsWith('tamepulse.approval.'),
            visible : hasTamepulseModule('approvals'),
            badge   : pendingApprovalCount.value
        },
        {
            name    : t('tamepulse.navigation.agents'),
            href    : router.resolve({ name: 'tamepulse.agent.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : CpuChipIcon,
            current : route.name?.startsWith('tamepulse.agent.'),
            visible : hasTamepulseModule('agents')
        },
        {
            name    : t('tamepulse.navigation.agentProfiles'),
            href    : router.resolve({ name: 'tamepulse.agentProfile.index', params: { tenantId: route.params.tenantId } }).href,
            icon    : UserGroupIcon,
            current : route.name?.startsWith('tamepulse.agentProfile.'),
            visible : hasTamepulseModule('agents')
        },
    ]
}

const getToolNavigation      = () => {

    let navigation       = [
        {
            name        : 'Product types',
            href        : router.resolve({ name: 'product-type.index' }).href,
            icon        : CubeIcon,
            current     : route.name === 'product-type.index',
            visible     : isNicheraTenant()
        },

        {
            name        : 'Product attributes',
            href        : router.resolve({ name: 'product-attribute.index' }).href,
            icon        : TableCellsIcon,
            current     : route.name === 'product-attribute.index',
            visible     : isNicheraTenant()
        },

        {
            name        : 'Notification settings',
            href        : router.resolve({ name: 'notification.index' }).href,
            icon        : WrenchIcon,
            current     : route.name === 'notification.index',
            visible     : isNicheraTenant()
        },

        {
            name        : 'Currency rates',
            href        : router.resolve({ name: 'currency-rate.index' }).href,
            icon        : CurrencyDollarIcon,
            current     : route.name === 'currency-rate.index',
            visible     : isNicheraTenant()
        },

        {
            name        : t('tamepulse.navigation.tasks'),
            href        : router.resolve({ name: 'task.index' }).href,
            icon        : RectangleStackIcon,
            current     : route.name === 'task.index',
            visible     : true,
        },

        {
            name        : 'API Tokens',
            href        : router.resolve({ name: 'public-token.edit' }).href,
            icon        : LockClosedIcon,
            current     : route.name === 'public-token.edit',
            visible     : isNicheraTenant()
        }
    ];

    return navigation
}

const getModuleNavigation      = () => {

    let navigation       = []

    const isNichera = isNicheraTenant()

    navigation.push({
        name        : 'Attachments',
        href        : router.resolve({ name: 'attachment.index' }).href,
        icon        : PaperClipIcon,
        current     : route.name === 'attachment.index',
        visible     : isNichera,
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.families'),
        href        : router.resolve({ name: 'problem-discovery.family.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : RectangleGroupIcon,
        current     : route.name?.startsWith('problem-discovery.family.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.problems'),
        href        : router.resolve({ name: 'problem-discovery.problem.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : ClipboardDocumentListIcon,
        current     : route.name?.startsWith('problem-discovery.problem.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.customerProfiles'),
        href        : router.resolve({ name: 'problem-discovery.customer-profile.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : UserGroupIcon,
        current     : route.name?.startsWith('problem-discovery.customer-profile.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.feedback'),
        href        : router.resolve({ name: 'problem-discovery.feedback.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : ChatBubbleLeftRightIcon,
        current     : route.name?.startsWith('problem-discovery.feedback.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.sources'),
        href        : router.resolve({ name: 'problem-discovery.source.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : LinkIcon,
        current     : route.name?.startsWith('problem-discovery.source.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.sourcePages'),
        href        : router.resolve({ name: 'problem-discovery.source-page.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : DocumentTextIcon,
        current     : route.name?.startsWith('problem-discovery.source-page.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.sourceEvidence'),
        href        : router.resolve({ name: 'problem-discovery.evidence.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : ScaleIcon,
        current     : route.name?.startsWith('problem-discovery.evidence.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.players'),
        href        : router.resolve({ name: 'problem-discovery.player.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : BuildingStorefrontIcon,
        current     : route.name?.startsWith('problem-discovery.player.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.marketSolutions'),
        href        : router.resolve({ name: 'problem-discovery.market-solution.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : ShoppingBagIcon,
        current     : route.name?.startsWith('problem-discovery.market-solution.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.solutions'),
        href        : router.resolve({ name: 'problem-discovery.solution.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : WrenchIcon,
        current     : route.name?.startsWith('problem-discovery.solution.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.assets'),
        href        : router.resolve({ name: 'problem-discovery.asset.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : CubeIcon,
        current     : route.name?.startsWith('problem-discovery.asset.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : t('problemDiscovery.navigation.solutionAssetRequirements'),
        href        : router.resolve({ name: 'problem-discovery.solution-asset-requirement.index', params: { tenantId: route.params.tenantId } }).href,
        icon        : ChartBarIcon,
        current     : route.name?.startsWith('problem-discovery.solution-asset-requirement.'),
        visible     : isProblemDiscoveryEnabled(),
    })

    navigation.push({
        name        : 'Color groups',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'color_group' } }).href,
        icon        : SwatchIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'color_group',
        visible     : isNichera,
    })

    navigation.push({
        name        : 'Colors',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'color' } }).href,
        icon        : PaintBrushIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'color',
        visible     : isNichera,
    })

    navigation.push({
        name        : 'Print costs',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'print_cost' } }).href,
        icon        : ScaleIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'print_cost',
        visible     : isNichera,
    })

    navigation.push({
        name        : 'Print techniques',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'print_technique' } }).href,
        icon        : PrinterIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'print_technique',
        visible     : isNichera,
    })

    navigation.push({
        name        : 'Print handling charge',
        href        : router.resolve({ name: 'print-handling-charge.index' }).href,
        icon        : DocumentCurrencyDollarIcon,
        current     : route.name === 'print-handling-charge.index',
        visible     : isNichera,
    })

    navigation.push({
        name        : 'Certificates',
        href        : router.resolve({ name: 'entity.index', params: { entityType: 'certificate' } }).href,
        icon        : DocumentTextIcon,
        current     : route.name?.startsWith('entity.') && route.params?.entityType === 'certificate',
        visible     : isNichera,
    })

    return navigation
}
const hasLocalizeService         = () => {

    return false;

    let hasLocalize    = false

    /*
    for(const userService of usePage().props.user.services){

        if(userService.service._id == '67978ac31c6d6545730aa6f2'){
            hasLocalize     = true
        }
    }
    */

    return hasLocalize
}

const getLocalizeNavigation      = () => {

    let navigation       = []

    navigation.push({
        name        : 'String translation',
        href        : '#',
        icon        : LanguageIcon,
        current     : ''
    });

    return navigation
}


</script>
